import { useCallback, useEffect, useState } from 'react'
import type { AppError, DailyHealth } from '@/types'
import { repositories } from '@/services/repositories'
import { isAppError } from '@/lib/supabase/errors'
import { formatDutchDate } from '@/domain/recurrence'
import { Banner, Button, Card, EmptyState, Skeleton } from '@/components/ui'
import { HeartIcon } from '@/components/icons'
import { errorMessage } from '../saveState'

const HISTORY_DAYS = 7

type RecentHistoryProps = {
  /** yyyy-mm-dd of today; today's entry is listed first, not duplicated. */
  today: string
}

/** Recent daily health records (hydration, sleep, quality, notes). */
export function RecentHistory({ today }: RecentHistoryProps) {
  const [entries, setEntries] = useState<DailyHealth[] | null>(null)
  const [error, setError] = useState<AppError | null>(null)

  const load = useCallback(async () => {
    try {
      const loaded = await repositories.health.listRecentDaily(HISTORY_DAYS)
      const sorted = [...loaded].sort((a, b) => (a.date < b.date ? 1 : -1))
      setEntries(sorted)
      setError(null)
    } catch (loadError) {
      setEntries([])
      setError(
        isAppError(loadError)
          ? loadError
          : {
              code: 'UNKNOWN',
              userMessage: errorMessage(loadError),
              retryable: true,
              source: 'database',
            },
      )
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="health-section" aria-labelledby="health-history-title">
      <h2 className="health-section-title" id="health-history-title">
        <HeartIcon size={20} className="health-section-icon" />
        Recente dagen
      </h2>

      {entries === null ? (
        <Skeleton lines={3} />
      ) : error ? (
        <Banner tone="error">
          {error.userMessage}{' '}
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            Opnieuw proberen
          </Button>
        </Banner>
      ) : entries.length === 0 ? (
        <EmptyState
          title="Nog geen eerdere dagen"
          body="Zodra je je gezondheid opslaat, verschijnen eerdere dagen hier."
        />
      ) : (
        <ul className="history-list">
          {entries.map((entry) => (
            <li key={entry.id}>
              <Card padding="sm">
                <p className="history-item-date">
                  {formatDutchDate(entry.date)}
                  {entry.date === today ? ' (vandaag)' : ''}
                </p>
                <p className="history-item-facts">
                  {entry.hydration} van 8 glazen · {entry.sleepHours} uur slaap · kwaliteit{' '}
                  {entry.sleepQuality} van 5
                </p>
                {entry.notes ? <p className="history-item-notes">{entry.notes}</p> : null}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
